import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import styles from './UserManagementDashboard.module.css';
import { 
  FaSearch, FaChartBar, FaEdit, FaSpinner, FaUserShield, FaCrown, 
  FaGraduationCap, FaLock, FaTimes, FaUsers, FaChevronLeft, 
  FaChevronRight, FaUserSlash, FaEnvelope, FaPhoneAlt, FaCalendarAlt,
  FaClipboardList
} from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function UserManagementDashboard() {
  const { userData } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [granting, setGranting] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(false);

  // Test Results Modal
  const [testResultsUser, setTestResultsUser] = useState(null);
  const [testResultsData, setTestResultsData] = useState([]);
  const [testResultsLoading, setTestResultsLoading] = useState(false);
  const [testResultsDateFilter, setTestResultsDateFilter] = useState('all');
  const [testResultsPage, setTestResultsPage] = useState(1);
  const [testResultsPerPage, setTestResultsPerPage] = useState(10);
  
  const [analytics, setAnalytics] = useState({
    totalUsers: 0, totalAdmins: 0, totalSuperAdmins: 0, totalPremium: 0, totalNormal: 0
  });

  const isSuperadmin = userData?.role === 'superadmin';

  const openTestResults = async (user) => {
    setTestResultsUser(user);
    setTestResultsData([]);
    setTestResultsDateFilter('all');
    setTestResultsPage(1);
    setTestResultsLoading(true);
    try {
      const snap = await getDoc(doc(db, 'users', user.id));
      if (snap.exists()) {
        const data = snap.data();
        const tests = (data.testsTaken || []).slice().sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setTestResultsData(tests);
      }
    } catch (e) {
      toast.error('Failed to load test results');
    } finally {
      setTestResultsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersCol = collection(db, "users");
      const snapshot = await getDocs(usersCol);
      const userDataList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(userDataList);
      
      setAnalytics({
        totalUsers: userDataList.length,
        totalAdmins: userDataList.filter(u => u.role === 'admin').length,
        totalSuperAdmins: userDataList.filter(u => u.role === 'superadmin').length,
        totalPremium: userDataList.filter(u => Array.isArray(u.purchasedCourses) && u.purchasedCourses.length > 0).length,
        totalNormal: userDataList.filter(u => !Array.isArray(u.purchasedCourses) || u.purchasedCourses.length === 0).length
      });
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userData) fetchUsers(); }, [userData]);

  // Filter config for maintainability
  const filterConfig = [
    { key: 'all', fn: u => true },
    { key: 'admins', fn: u => u.role === 'admin' },
    { key: 'superadmins', fn: u => u.role === 'superadmin' },
    { key: 'premium', fn: u => Array.isArray(u.purchasedCourses) && u.purchasedCourses.length > 0 },
    { key: 'normal', fn: u => !Array.isArray(u.purchasedCourses) || u.purchasedCourses.length === 0 },
  ];

  const getDateLimit = () => {
    if (dateFilter === 'all') return null;
    const months = { '30d': null, '60d': null, '1y': null, '2y': null };
    const d = new Date();
    if (dateFilter === '30d') d.setDate(d.getDate() - 30);
    else if (dateFilter === '60d') d.setDate(d.getDate() - 60);
    else if (dateFilter === '1y') d.setFullYear(d.getFullYear() - 1);
    else if (dateFilter === '2y') d.setFullYear(d.getFullYear() - 2);
    return d;
  };

  useEffect(() => {
    let result = [...users];
    const filterObj = filterConfig.find(f => f.key === activeFilter);
    if (filterObj) result = result.filter(filterObj.fn);

    const dateLimit = getDateLimit();
    if (dateLimit) {
      result = result.filter(u => {
        const ts = u.createdAt;
        if (!ts) return false;
        let d;
        if (typeof ts === 'object' && ts !== null && typeof ts.seconds === 'number') {
          d = new Date(ts.seconds * 1000);
        } else if (typeof ts === 'string' || typeof ts === 'number') {
          d = new Date(ts);
        } else return false;
        return !isNaN(d.getTime()) && d >= dateLimit;
      });
    }

    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      result = result.filter(u =>
        (u.username || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s)
      );
    }
    setFilteredUsers(result);
    setPage(1);
  }, [searchTerm, activeFilter, dateFilter, users]);

  const handleGrantAccess = async (accessKey) => {
    if (!isSuperadmin) return;
    if (!window.confirm(`Are you sure you want to grant ${accessKey} access to ${grantEmail}?`)) {
      toast('Grant cancelled');
      return;
    }
    setGranting(true);
    try {
      const userToGrant = users.find(u => u.email?.toLowerCase() === grantEmail.toLowerCase());
      if (!userToGrant) throw new Error('User not found');
      if (userToGrant.dashboardAccess && userToGrant.dashboardAccess[accessKey]) {
        toast.error('User already has this access');
        setGranting(false);
        return;
      }
      const update = { dashboardAccess: { ...userToGrant.dashboardAccess, [accessKey]: true } };
      await updateDoc(doc(db, 'users', userToGrant.id), update);
      toast.success(`${accessKey.charAt(0).toUpperCase() + accessKey.slice(1)} access granted to ${userToGrant.email}`);
      setGrantEmail(''); setShowGrantModal(false); fetchUsers();
    } catch (error) { toast.error(error.message); } finally { setGranting(false); }
  };

  const handleRevokeAccess = async (user, accessKey) => {
    if (!isSuperadmin) return;
    if (!window.confirm(`Are you sure you want to revoke ${accessKey} access from ${user.email}?`)) {
      toast('Revoke cancelled');
      return;
    }
    try {
      const updatedAccess = { ...user.dashboardAccess };
      delete updatedAccess[accessKey]; 
      await updateDoc(doc(db, 'users', user.id), { dashboardAccess: updatedAccess });
      toast.success(`${accessKey.charAt(0).toUpperCase() + accessKey.slice(1)} access revoked from ${user.email}`); fetchUsers();
    } catch (error) { toast.error('Failed to revoke access'); }
  };

  const handleRoleChange = async (uid, newRole) => {
    if (!isSuperadmin) return;
    const user = users.find(u => u.id === uid);
    if (!user) {
      toast.error('User not found');
      return;
    }
    if (!window.confirm(`Are you sure you want to change the role of ${user.email} to ${newRole}?`)) {
      toast('Role change cancelled');
      return;
    }
    setUpdatingRole(true);
    try {
      await updateDoc(doc(db, 'users', uid), { role: newRole, isAdmin: newRole !== 'student', isSuperAdmin: newRole === 'superadmin' });
      toast.success(`${user.email} is now ${newRole}`); setShowRoleModal(false); fetchUsers();
    } catch (error) { toast.error('Failed to update role'); } finally { setUpdatingRole(false); }
  };

  // Robust date formatting for Firestore Timestamp, ISO string, or Date
  const formatDate = (ts) => {
    if (!ts) return 'N/A';
    let d;
    if (typeof ts === 'object' && ts !== null && typeof ts.seconds === 'number') {
      d = new Date(ts.seconds * 1000);
    } else if (typeof ts === 'string' && !isNaN(Date.parse(ts))) {
      d = new Date(ts);
    } else if (typeof ts === 'number') {
      d = new Date(ts);
    } else {
      return 'N/A';
    }
    if (isNaN(d.getTime())) {
      return 'Invalid date';
    }
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (!userData) return <div className={styles.accessDenied}>Please Log In</div>;
  // For future: If user count is very large, fetch paginated data from server/Firestore
  // Placeholder for server-side pagination logic
  // const [serverTotal, setServerTotal] = useState(0);
  // const [serverUsers, setServerUsers] = useState([]);
  // const fetchPage = async (page, pageSize) => { ... };

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.titleArea}>
          <h1>User Management</h1>
          <p>Administration for <b>PrrepX Pathshala</b></p>
        </div>
        {isSuperadmin && (
          <div className={styles.grantAccessGroup}>
            <button aria-label="Grant User Management Access" onClick={() => setShowGrantModal('userManagement')}><FaUserShield /> UserMgmt</button>
            <button aria-label="Grant Dashboard Access" onClick={() => setShowGrantModal('dashboard')}><FaChartBar /> Dashboard</button>
            <button aria-label="Grant Invoices Access" onClick={() => setShowGrantModal('invoices')}><FaLock /> Sales&Invoices</button>
          </div>
        )}
      </header>

      <div className={styles.analyticsGrid}>
        {[
          { key: 'all', icon: <FaUsers />, label: 'Total Users', count: analytics.totalUsers, color: styles.blue },
          { key: 'admins', icon: <FaUserShield />, label: 'Admins', count: analytics.totalAdmins, color: styles.green },
          { key: 'superadmins', icon: <FaCrown />, label: 'SuperAdmins', count: analytics.totalSuperAdmins, color: styles.gold },
          { key: 'premium', icon: <FaGraduationCap />, label: 'Premium', count: analytics.totalPremium, color: styles.purple },
          { key: 'normal', icon: <FaUserSlash />, label: 'Normal', count: analytics.totalNormal, color: styles.orange },
        ].map((item) => (
          <div key={item.key} className={`${styles.statCard} ${item.color} ${activeFilter === item.key ? styles.activeCard : ''}`} onClick={() => setActiveFilter(item.key)}>
            {item.icon} <div><h3>{item.count}</h3><p>{item.label}</p></div>
          </div>
        ))}
      </div>

      <div className={styles.tableControls}>
        <div className={styles.searchBar}>
          <FaSearch />
          <input type="text" placeholder="Search users..." aria-label="Search users" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <select
          aria-label="Filter by join date"
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
          className={styles.dateFilterSelect}
        >
          <option value="all">All Time</option>
          <option value="30d">Last 30 Days</option>
          <option value="60d">Last 60 Days</option>
          <option value="1y">Last Year</option>
          <option value="2y">Last 2 Years</option>
        </select>
      </div>

      {loading ? (
        <div className={styles.loader}><FaSpinner className={styles.spin} /> <span>Fetching Cloud Data...</span></div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.mainTable} aria-label="User Management Table">
            <thead>
              <tr>
                <th>Profile & Status</th>
                <th>Academic</th>
                <th>Role</th>
                <th>Status</th>
                <th>Permissions</th>
                <th>Created At</th>
                {isSuperadmin && <th>Edit Role</th>}                <th>Tests</th>              </tr>
            </thead>
            <tbody>
              {/* For server-side pagination, replace filteredUsers.slice(...) with serverUsers */}
              {filteredUsers.slice((page-1)*pageSize, page*pageSize).map(user => (
                <tr key={user.id}>
                  <td>
                    <div className={styles.primaryInfo}>
                      <div className={styles.nameRow}>
                        <span className={styles.userName}>{user.username || user.displayName || "-"}</span>
                      </div>
                      <span className={styles.userEmail}><FaEnvelope /> {user.email}</span>
                      <span className={styles.userPhone}><FaPhoneAlt /> {user.mobile || '-'}</span>
                    </div>
                  </td>
                  <td><div className={styles.subInfo}>{user.class || 'N/A'} | {user.preparingFor || 'N/A'}</div></td>
                  <td><span className={`${styles.roleTag} ${styles[user.role]}`}>{user.role}</span></td>
                  <td>{Array.isArray(user.purchasedCourses) && user.purchasedCourses.length > 0 ? <span className={styles.premiumTag}>Premium</span> : <span className={styles.normalTag}>Normal</span>}</td>
                  <td>
                    <div className={styles.accessList}>
                      {user.dashboardAccess && (
                        <>
                          {user.dashboardAccess.userManagement && (
                            <span className={styles.accessBadge}>
                              Mgmt
                              {isSuperadmin && user.role !== 'superadmin' && (
                                <button aria-label="Revoke userManagement access" onClick={() => handleRevokeAccess(user, 'userManagement')}><FaTimes /></button>
                              )}
                            </span>
                          )}
                          {user.dashboardAccess.dashboard && (
                            <span className={styles.accessBadge}>
                              Dashboard
                              {isSuperadmin && user.role !== 'superadmin' && (
                                <button aria-label="Revoke dashboard access" onClick={() => handleRevokeAccess(user, 'dashboard')}><FaTimes /></button>
                              )}
                            </span>
                          )}
                          {user.dashboardAccess.invoices && (
                            <span className={styles.accessBadge}>
                              Invoices
                              {isSuperadmin && user.role !== 'superadmin' && (
                                <button aria-label="Revoke invoices access" onClick={() => handleRevokeAccess(user, 'invoices')}><FaTimes /></button>
                              )}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.dateCell}>
                      <FaCalendarAlt /> {formatDate(user.createdAt)}
                    </div>
                  </td>
                  {isSuperadmin && <td>{user.role !== 'superadmin' && <button className={styles.editBtn} aria-label="Edit user role" onClick={() => { setSelectedUserForRole(user); setShowRoleModal(true); }}><FaEdit /></button>}</td>}
                  <td>
                    <button
                      className={styles.testResultsBtn}
                      onClick={() => openTestResults(user)}
                      title="View test results"
                    >
                      <FaClipboardList />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <footer className={styles.paginationFooter}>
        <div className={styles.pageSizeSelect}>
          <select aria-label="Select page size" value={pageSize} onChange={e => {setPageSize(Number(e.target.value)); setPage(1);}}>
            <option value={10}>10 Users</option><option value={20}>20 Users</option><option value={50}>50 Users</option>
          </select>
        </div>
        <div className={styles.pageButtons}>
          <button aria-label="Previous page" disabled={page === 1} onClick={() => setPage(p => p - 1)}><FaChevronLeft /></button>
          <span>{page} / {totalPages}</span>
          <button aria-label="Next page" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><FaChevronRight /></button>
        </div>
      </footer>

      {isSuperadmin && showGrantModal && (
        <div className={styles.modalBackdrop}><div className={styles.modalWrapper}>
          <button className={styles.closeBtn} aria-label="Close grant access modal" onClick={() => setShowGrantModal(false)}><FaTimes /></button>
          <h3>Grant {showGrantModal}</h3>
          <input type="email" placeholder="Email Address" aria-label="Grant access email address" value={grantEmail} onChange={e => setGrantEmail(e.target.value)} className={styles.modalInput} />
          <button className={styles.modalSubmit} aria-label="Grant access now" onClick={() => handleGrantAccess(showGrantModal)} disabled={granting}>{granting ? 'Processing...' : 'Grant Now'}</button>
        </div></div>
      )}

      {isSuperadmin && showRoleModal && (
        <div className={styles.modalBackdrop}><div className={styles.modalWrapper}>
          <button className={styles.closeBtn} aria-label="Close change role modal" onClick={() => setShowRoleModal(false)}><FaTimes /></button>
          <h3>Change Role</h3>
          <select className={styles.modalInput} aria-label="Select new user role" value={selectedUserForRole?.role} onChange={e => handleRoleChange(selectedUserForRole.id, e.target.value)} disabled={updatingRole}>
            <option value="student">Student</option><option value="admin">Admin Staff</option><option value="superadmin">Superadmin Owner</option>
          </select>
        </div></div>
      )}

      {/* ========== TEST RESULTS MODAL ========== */}
      {testResultsUser && (() => {
        const getDateLimit = () => {
          if (testResultsDateFilter === 'all') return null;
          const d = new Date();
          if (testResultsDateFilter === '30d') d.setDate(d.getDate() - 30);
          else if (testResultsDateFilter === '3m') d.setMonth(d.getMonth() - 3);
          else if (testResultsDateFilter === '6m') d.setMonth(d.getMonth() - 6);
          else if (testResultsDateFilter === '1y') d.setFullYear(d.getFullYear() - 1);
          else if (testResultsDateFilter === '2y') d.setFullYear(d.getFullYear() - 2);
          return d;
        };
        const dateLimit = getDateLimit();
        const filtered = testResultsData.filter(t => {
          if (!dateLimit) return true;
          const d = t.date ? new Date(t.date) : null;
          return d && !isNaN(d) && d >= dateLimit;
        });
        const totalPages = Math.max(1, Math.ceil(filtered.length / testResultsPerPage));
        const safePage = Math.min(testResultsPage, totalPages);
        const paginated = filtered.slice((safePage - 1) * testResultsPerPage, safePage * testResultsPerPage);
        const userName = testResultsUser.username || testResultsUser.email || testResultsUser.id;

        return (
          <div className={styles.trModalBackdrop} onClick={() => setTestResultsUser(null)}>
            <div className={styles.trModalBox} onClick={e => e.stopPropagation()}>

              <div className={styles.trModalHeader}>
                <div>
                  <h2 className={styles.trModalTitle}><FaClipboardList /> Test Results</h2>
                  <p className={styles.trModalSub}>
                    <strong>{userName}</strong> &mdash; {testResultsData.length} total attempt{testResultsData.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button className={styles.trCloseBtn} onClick={() => setTestResultsUser(null)}><FaTimes /></button>
              </div>

              <div className={styles.trFilters}>
                <label className={styles.trFilterLabel}>Filter by date:</label>
                <select
                  value={testResultsDateFilter}
                  onChange={e => { setTestResultsDateFilter(e.target.value); setTestResultsPage(1); }}
                  className={styles.trSelect}
                >
                  <option value="all">All Time</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="3m">Last 3 Months</option>
                  <option value="6m">Last 6 Months</option>
                  <option value="1y">Last Year</option>
                  <option value="2y">Last 2 Years</option>
                </select>
                <span className={styles.trCount}>
                  {filtered.length}{testResultsDateFilter !== 'all' ? ` of ${testResultsData.length}` : ''} result{filtered.length !== 1 ? 's' : ''}
                </span>
              </div>

              {testResultsLoading ? (
                <div className={styles.trLoading}><FaSpinner className={styles.spin} style={{marginRight:8}}/> Loading results...</div>
              ) : filtered.length === 0 ? (
                <p className={styles.trEmpty}>
                  {testResultsData.length === 0
                    ? 'This student has not attempted any test yet.'
                    : 'No results found for the selected time period.'}
                </p>
              ) : (
                <>
                  <div className={styles.trTableWrap}>
                    <table className={styles.trTable}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Test Name</th>
                          <th>Score / Max</th>
                          <th>Attempted</th>
                          <th>Correct</th>
                          <th>Incorrect</th>
                          <th>Attempt No.</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.map((t, idx) => {
                          const pct = t.total > 0 ? Math.round((t.score / t.total) * 100) : null;
                          return (
                            <tr key={idx}>
                              <td>{(safePage - 1) * testResultsPerPage + idx + 1}</td>
                              <td className={styles.trTestName}>{t.testName || 'N/A'}</td>
                              <td>
                                <span className={t.score >= 0 ? styles.trGreen : styles.trRed}>
                                  {t.score ?? 'N/A'} / {t.total ?? 'N/A'}
                                </span>
                                {pct !== null && <span className={styles.trPct}> ({pct}%)</span>}
                              </td>
                              <td>{t.attempted ?? 'N/A'} / {t.totalQuestions ?? 'N/A'}</td>
                              <td><span className={styles.trGreen}>{t.correct ?? 'N/A'}</span></td>
                              <td><span className={styles.trRed}>{t.incorrect ?? 'N/A'}</span></td>
                              <td>{t.attempt ?? '—'}</td>
                              <td>{t.date ? new Date(t.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className={styles.trPagination}>
                    <span className={styles.trPagInfo}>
                      Showing {(safePage - 1) * testResultsPerPage + 1}–{Math.min(safePage * testResultsPerPage, filtered.length)} of {filtered.length}
                    </span>
                    <div className={styles.trPagControls}>
                      <button className={styles.trPageBtn} onClick={() => setTestResultsPage(1)} disabled={safePage === 1}>«</button>
                      <button className={styles.trPageBtn} onClick={() => setTestResultsPage(p => p - 1)} disabled={safePage === 1}>‹ Prev</button>
                      <span className={styles.trPageInfo}>Page {safePage} of {totalPages}</span>
                      <button className={styles.trPageBtn} onClick={() => setTestResultsPage(p => p + 1)} disabled={safePage >= totalPages}>Next ›</button>
                      <button className={styles.trPageBtn} onClick={() => setTestResultsPage(totalPages)} disabled={safePage >= totalPages}>»</button>
                      <select
                        value={testResultsPerPage}
                        onChange={e => { setTestResultsPerPage(Number(e.target.value)); setTestResultsPage(1); }}
                        className={styles.trPerPage}
                      >
                        <option value={10}>10 / page</option>
                        <option value={20}>20 / page</option>
                        <option value={50}>50 / page</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>
        );
      })()}

    </div>
  );
}